import React from 'react';
import * as jobsApi from '@/lib/api/jobs';

interface JobMaterialsListProps {
  jobId: string;
  onTotalCostChange?: (totalCost: number) => void;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);

const formatDate = (dateString: string) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const JobMaterialsList: React.FC<JobMaterialsListProps> = ({ jobId, onTotalCostChange }) => {
  const [materials, setMaterials] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const loadMaterials = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await jobsApi.getJobMaterials(jobId);
      const arr = Array.isArray(data) ? data : [];
      setMaterials(arr);
      const total = arr.reduce((sum, m) => sum + (parseFloat(m.total_cost as string) || 0), 0);
      onTotalCostChange?.(total);
    } catch (err: unknown) {
      const e = err as { message?: string };
      setError(e.message || 'Failed to load materials');
      setMaterials([]);
    } finally {
      setLoading(false);
    }
  }, [jobId, onTotalCostChange]);

  React.useEffect(() => { loadMaterials(); }, [loadMaterials]);

  const totalCost = materials.reduce((sum, m) => sum + (parseFloat(m.total_cost) || 0), 0);

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map(i => <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3">
        <span className="material-symbols-outlined text-4xl text-red-300">error</span>
        <p className="text-sm text-gray-500">{error}</p>
        <button onClick={loadMaterials} className="text-sm font-semibold text-primary border border-primary/30 rounded-lg px-4 py-2 hover:bg-primary/5">Retry</button>
      </div>
    );
  }

  if (materials.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
        <span className="material-symbols-outlined text-gray-300 text-5xl block mb-3">inventory_2</span>
        <p className="text-sm font-medium text-gray-500">No materials tracked yet</p>
        <p className="text-xs text-gray-400 mt-1">Add materials used on this job to track costs</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Total Materials Cost</p>
          <p className="text-2xl font-bold text-primary">{formatCurrency(totalCost)}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Line Items</p>
          <p className="text-2xl font-bold text-gray-900">{materials.length}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Avg. Cost / Item</p>
          <p className="text-2xl font-bold text-gray-900">
            {materials.length > 0 ? formatCurrency(totalCost / materials.length) : '—'}
          </p>
        </div>
      </div>

      {/* Materials table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[540px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Material</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Qty</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Unit Cost</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Total</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Date Used</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Added By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {materials.map((material) => (
                <tr key={material.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{material.material_name}</p>
                    {material.notes && <p className="text-xs text-gray-400 mt-0.5">{material.notes}</p>}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">
                    {material.quantity_used} {material.unit}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">
                    {material.cost_per_unit ? formatCurrency(parseFloat(material.cost_per_unit)) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-900">
                    {formatCurrency(parseFloat(material.total_cost) || 0)}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{formatDate(material.date_used)}</td>
                  <td className="px-4 py-3 text-gray-500">{material.added_by_name || '—'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-gray-200 bg-gray-50">
              <tr>
                <td className="px-4 py-3 font-bold text-gray-900 text-sm" colSpan={3}>Total</td>
                <td className="px-4 py-3 text-right font-bold text-primary text-sm">{formatCurrency(totalCost)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};
